<template>
    <kgr-accordion-item
        ref="accordionItem"
        :overflow="true"
        :index="index"
    >
        <template #header-left>
            <div class="kgr-postfix kgr-postfix--center kgr-postfix--tinyGutters row-min-height">
                <div class="kgr-postfix__fixed">
                    <div class="kgr-accordion__itemHeadline">
                        {{ appointmentTitle }}
                    </div>
                </div>
                <div
                    v-if="isOverwritten"
                    class="kgr-postfix__fixed"
                >
                    <kgr-icon
                        icon-class="warning"
                        :title="$t('Dieser Termin hat Änderungen')"
                        class="kgr-postfix__icon"
                    />
                </div>
                <div class="kgr-postfix__fixed kgr-accordion__itemHeadline row-extra-margin">
                    <button
                        v-show="offering.canCreateOfferingBookings && !showAddParticipant"
                        :title="isDogExam ? $t('Hund hinzufügen') : $t('Teilnehmer hinzufügen')"
                        class="kgr-color--accent"
                        @click="editParticipant"
                    >
                        <kgr-icon
                            icon-class="add"
                            class="add-icon"
                            small
                        />
                        <kgr-icon
                            v-if="!isDogExam"
                            icon-class="user"
                            class="add-icon-user"
                            small
                        />
                        <kgr-icon
                            v-else
                            icon-class="pets"
                            class="add-icon-dogs"
                            small
                        />
                    </button>
                    <div
                        v-if="showAddParticipant"
                        class="search-container"
                        @click.stop
                    >
                        <kgr-offering-live-search-result-preview
                            :club-id="clubId"
                            :offering="offering"
                            :default-placeholder="isDogExam"
                            :is-dog-search="isDogExam"
                            :loading-add-participant="loadingAddParticipant"
                            @member-selected="(data) => saveParticipant(data)"
                            @dog-and-member-selected="(data) => saveDogAndParticipant(data)"
                            @update:selected-sales-list-offering-items="selectedSalesListOfferingItems = $event"
                            @open-modal="showSelectedParticipantModal = true"
                            @close-modal="showSelectedParticipantModal = false"
                        />
                    </div>
                    <button
                        v-show="showAddParticipant"
                        :title="$t('Abbrechen')"
                        class="clear-icon"
                        @click.stop="cancelAddParticipant"
                    >
                        <kgr-icon icon-class="clear" />
                    </button>
                </div>
                <div class="kgr-postfix__fixed kgr-postfix__fluid"></div>
                <div
                    v-if="appointment.attendanceAbsentCount !== null"
                    class="kgr-postfix__fixed kgr-postfix__fluid"
                    style="text-align: right"
                >
                    {{ $t('booking.attendeeStatus') }}: <span style="color: green"> {{ appointment.attendancePresentCount }} </span>
                    / <span> {{ appointment.attendanceNoneCount }}</span>
                    / <span style="color: red">{{ appointment.attendanceAbsentCount }}</span>
                </div>
            </div>
        </template>

        <div v-if="active">
            <div class="kgr-row">
                <button
                    v-if="isCancelled"
                    class="kgr-button kgr-button--accent"
                    @click="openConfirmReactivationModal()"
                >
                    {{ $t('Diesen Termin reaktivieren') }}
                </button>
                <button
                    v-else
                    class="kgr-button kgr-button--accent"
                    @click="openConfirmCancellationModal()"
                >
                    {{ $t('Diesen Termin absagen') }}
                </button>
                <button
                    v-show="!appointment.appointmentOverride || (appointment.appointmentOverride && !appointment.appointmentOverride.isCanceled)"
                    class="kgr-button kgr-button--accent"
                    @click="showEdit = !showEdit"
                >
                    {{ $t('Diesen Termin bearbeiten') }}
                </button>
                <button
                    v-if="isStartedOrPassedAppointment"
                    class="kgr-button kgr-button--accent"
                    @click="showAttendanceList"
                >
                    {{ $t('Anwesenheitsliste anzeigen') }}
                </button>
                <button
                    v-if="showExamResultButton"
                    :disabled="disableExamResultsButton"
                    class="kgr-button kgr-button--accent"
                    @click="createExamResults()"
                >
                    {{ $t('Prüfungsergebnisse erfassen') }}
                </button>
            </div>
            <kgr-appointment-overrides
                :appointment-prop="appointment"
                :error="error"
                :show-edit="showEdit"
                :instructors="instructors"
                :club-id="clubId"
                @cancel-edit="showEdit = false;"
                @update-appointment="updateAppointment(appointment)"
            />
            <div
                v-if="pending && !showParticipantTable"
                class="kgr-row"
            >
                <kgr-loading inline />
            </div>

            <div
                v-if="showParticipantTable"
                class="kgr-row"
            >
                <kgr-generic-appointment-participant-table
                    v-if="participants.length > 0"
                    ref="participantsTable"
                    :pending="pending"
                    :headline="headlineParticipantsTable"
                    :bookings="participants"
                    :offering="offering"
                    :user-id="userId"
                    :table-columns="tableColumns"
                    can-reject
                    @select-all="toggleSelectAll('participate')"
                    @booking-checked="checkBooking($event)"
                    @cancel-booking="cancelBooking($event)"
                    @copy-mail-address="copyToClipboard($event, true)"
                    @update:offering-booking="$emit('update:offeringBooking', appointment)"
                />
                <kgr-generic-appointment-participant-table
                    v-if="cancelings.length > 0"
                    ref="cancelledParticipantsTable"
                    :pending="pending"
                    :offering="offering"
                    :user-id="userId"
                    :table-columns="tableColumns"
                    :headline="headlineCancelingsTable"
                    :bookings="cancelings"
                    table-for-cancellations
                    @select-all="toggleSelectAll('cancel')"
                    @booking-checked="checkBooking($event)"
                    @copy-mail-address="copyToClipboard($event, true)"
                />
                <kgr-generic-appointment-wait-list-table
                    v-if="waitList.length > 0"
                    ref="waitlistTable"
                    :offering="offering"
                    :pending="pending"
                    :headline="headlineWaitListTable"
                    :wait-list="waitListEntries"
                    :user-id="userId"
                    :table-columns="tableColumns"
                    @select-all="toggleSelectAll('waitList')"
                    @update-wait-list="updateWaitList"
                    @wait-list-checked="checkWaitList($event)"
                    @copy-mail-address="copyToClipboard($event, true)"
                />
                <div class="kgr-postfix__fixed">
                    <a
                        :href="`mailto:?bcc=${allMails}`"
                        target="_blank"
                        class="kgr-button kgr-button--accent"
                    >
                        {{ $t('E-Mail an Teilnehmer senden') }}
                    </a>
                    <button
                        class="kgr-button kgr-button--accent"
                        @click="copyToClipboard(allMails)"
                    >
                        {{ $t('E-Mail-Adressen der Teilnehmer kopieren') }}
                    </button>
                    <button
                        name="btn-pdf"
                        class="kgr-button kgr-button--accent"
                        @click="downloadPdf()"
                    >
                        {{ $t('Teilnehmerliste herunterladen (PDF)') }}
                    </button>
                    <button
                        name="btn-pdf"
                        class="kgr-button kgr-button--accent"
                        @click="downloadExcel()"
                    >
                        {{ $t('Teilnehmerliste herunterladen (Excel)') }}
                    </button>
                </div>
            </div>
            <kgr-empty-data
                v-else-if="!pending && offering.registrationRequested && !showEdit && !showAddParticipant"
                :value="$t('Keine Teilnehmer vorhanden')"
            />
            <kgr-empty-data
                v-else-if="!pending && !showEdit && !showAddParticipant"
                :value="$t('Keine Anmeldung erforderlich')"
            />
        </div>
        <kgr-exam-results-dialog
            v-if="offering.isExam()"
            :show-dialog="showDialog"
            :club-id="clubId"
            :exam-offering="offering"
            :club-exams="offering.clubExams"
            :offering-bookings="participants"
            :appointment="appointment"
            @close-dialog="closeDialog()"
        />
        <kgr-update-offering-with-notification-modal
            :show-dialog="showCancellationModal"
            :headline="$t('Soll dieser Termin storniert werden?')"
            show-booking-hint
            @confirm="onCancellationConfirm"
            @cancel="onCancelCancellation"
        />
        <kgr-update-offering-with-notification-modal
            :show-dialog="showReactivationModal"
            :headline="$t('Soll dieser Termin reaktiviert werden?')"
            @confirm="onReactivationConfirm"
            @cancel="onCancelReactivation"
        />
    </kgr-accordion-item>
</template>

<script>
import AccordionItem from '../../common/accordion/AccordionItem.vue';
import AppointmentOverrides from './AppointmentOverrides.vue';
import AppointmentRepository from '../../../js/repository/AppointmentRepository.js';
import EmptyData from '../../common/EmptyData.vue';
import Error from '../../../js/model/Error.js';
import ExamResultsDialog from './ExamResultsDialog.vue';
import GenericAppointmentParticipantTable from './GenericAppointmentParticipantTable.vue';
import GenericAppointmentWaitListTable from './GenericAppointmentWaitListTable.vue';
import KgrIcon from '../../common/icon/Icon.vue';
import Loading from '../../common/Loading.vue';
import Offering from '../../../js/dto/offering/Offering.js';
import OfferingAppointment from '../../../js/dto/offering/OfferingAppointment.js';
import OfferingBookingRepository from '../../../js/repository/club/offering/OfferingBookingRepository.js';
import OfferingLiveSearchResultPreview from '../OfferingLiveSearchResultPreview.vue';
import UpdateOfferingWithNotificationModal from '../UpdateOfferingWithNotificationModal.vue';
import moment from 'moment';

export default {
    name: 'KgrAppointmentAccordionItem',
    components: {
        'kgr-accordion-item': AccordionItem,
        'kgr-appointment-overrides': AppointmentOverrides,
        'kgr-empty-data': EmptyData,
        'kgr-exam-results-dialog': ExamResultsDialog,
        'kgr-generic-appointment-participant-table': GenericAppointmentParticipantTable,
        'kgr-generic-appointment-wait-list-table': GenericAppointmentWaitListTable,
        'kgr-icon': KgrIcon,
        'kgr-loading': Loading,
        'kgr-offering-live-search-result-preview': OfferingLiveSearchResultPreview,
        'kgr-update-offering-with-notification-modal': UpdateOfferingWithNotificationModal,
    },
    props: {
        appointmentProp: {
            type: OfferingAppointment,
            required: true,
        },
        bookings: {
            type: Array,
            required: true,
        },
        waitList: {
            type: Array,
            required: true,
        },
        instructors: {
            type: Array,
            required: true,
        },
        offering: {
            type: Offering,
            required: true,
        },
        clubId: {
            type: Number,
            required: true,
        },
        index: {
            type: Number,
            required: true,
        },
        active: {
            type: Boolean,
            required: true,
        },
        pending: {
            type: Boolean,
            required: true,
        },
        error: {
            type: Error,
            required: true,
        },
        userId: {
            type: Number,
            required: true,
        },
        tableColumns: {
            type: Array,
            required: true,
        },
        searchOpenIndex: {
            type: Number,
            default: null,
        },
    },
    data() {
        return {
            appointment: null,
            loading: false,
            showDetails: false,
            showEdit: false,
            selectAllParticipates: false,
            selectAllWaitList: false,
            selectAllCancelings: false,
            checkedBookings: [],
            checkedWaitListEntries: [],
            showAddParticipant: false,
            waitListUpdated: false,
            showDialog: false,
            showSelectedParticipantModal: false,
            loadingAddParticipant: false,
            selectedSalesListOfferingItems: [],
            showCancellationModal: false,
            showReactivationModal: false,
        };
    },
    computed: {
        isDogExam() {
            return this.offering.isExamForDogs();
        },
        showParticipantTable() {
            return (this.participants.length > 0 || this.cancelings.length > 0 || this.waitList.length > 0) && !this.showEdit && this.active;
        },
        isOverwritten() {
            return this.appointment.hasAppointmentOverride;
        },
        isCancelled() {
            return this.appointment.appointmentOverride && this.appointment.appointmentOverride.isCanceled;
        },
        headlineParticipantsTable() {
            return `${this.$t('Teilnehmer')} (${this.participants.length})`;
        },
        headlineCancelingsTable() {
            return `${this.$t('Stornierte Teilnehmer')} (${this.cancelings.length})`;
        },
        getActiveWaitListCount() {
            return this.waitList.filter((entry) => !entry.deletedAt).length;
        },
        headlineWaitListTable() {
            return `${this.$t('Warteliste')} (${this.getActiveWaitListCount})`;
        },
        participants() {
            const participants = [];
            this.bookings.forEach((booking) => {
                if (!booking.isCanceled) {
                    booking.checked = this.selectAllParticipates ? true : this.checkedBookings.indexOf(booking.id) > -1;
                    participants.push(booking);
                }
            });
            return participants;
        },
        cancelings() {
            const cancelings = [];
            this.bookings.forEach((booking) => {
                if (booking.isCanceled) {
                    booking.checked = this.selectAllCancelings ? true : this.checkedBookings.indexOf(booking.id) > -1;
                    cancelings.push(booking);
                }
            });
            return cancelings;
        },
        waitListEntries() {
            const waitListEntries = [];
            this.waitList.forEach((entry) => {
                entry.checked = entry.deletedAt ? false : this.selectAllWaitList ? true : this.checkedWaitListEntries.indexOf(entry.id) > -1;
                waitListEntries.push(entry);
            });
            return waitListEntries;
        },
        waitListCount() {
            return this.waitList.filter((entry) => !entry.deletedAt).length;
        },
        appointmentTitle() {
            let title = `${(this.appointment.appointmentOverride && this.appointment.appointmentOverride.isCanceled ? `${this.$t('appointment.canceled').toUpperCase()}: ` : '') + this.$t(`weekday_${this.appointment.weekday}`)}, ${
                this.formattedDateTime(this.appointment.start.date)} `
                + ` - ${
                    this.formattedDate(this.appointment.start.date) < this.formattedDate(this.appointment.end.date) ? this.formattedDateTime(this.appointment.end.date) : this.formattedTime(this.appointment.end.date)
                } `;

            title += ` ( ${this.appointment.bookingCount}`;
            if (this.waitListUpdated) {
                if (this.waitListCount > 0) {
                    title += ` (+${this.waitListCount})`;
                }
            } else {
                if (this.appointment.waitListCount > 0) {
                    title += ` (+${this.appointment.waitListCount})`;
                }
            }
            title += ` / ${this.offering.maxParticipants ? this.offering.maxParticipants : 0} )`;

            return title;
        },
        allMails() {
            const result = [];
            this.participants.forEach((booking) => {
                if (booking.checked) {
                    result.push(booking.member.email);
                }
            });
            this.cancelings.forEach((canceling) => {
                if (canceling.checked) {
                    result.push(canceling.member.email);
                }
            });
            this.waitListEntries.forEach((entry) => {
                if (entry.checked) {
                    result.push(entry.member.email);
                }
            });
            return result.join('; ');
        },
        isStartedOrPassedAppointment() {
            const today = moment().endOf('day');
            const appointmentDate = moment(this.appointment.start.date, 'YYYY-MM-DD HH:mm:ss');
            return appointmentDate < today;
        },
        showExamResultButton() {
            return this.offering.clubExams.length > 0;
        },
        disableExamResultsButton() {
            return this.participants.length === 0 && !this.showSelectedParticipantModal;
        },
        columnQuery() {
            const participantsCols = this.$refs.participantsTable ? this.$refs.participantsTable.getSelectedColumns() : null;
            const cancellationsCols = this.$refs.cancelledParticipantsTable ? this.$refs.cancelledParticipantsTable.getSelectedColumns() : null;
            const waitlistCols = this.$refs.waitlistTable ? this.$refs.waitlistTable.getSelectedColumns() : null;
            return `?participants=${participantsCols ? participantsCols.join(',') : ''
            }&cancellations=${cancellationsCols ? cancellationsCols.join(',') : ''
            }&waitlist=${waitlistCols ? waitlistCols.join(',') : ''}`;
        },
    },
    watch: {
        active() {
            this.toggleAccordion();
        },
        searchOpenIndex(newIndex) {
            if (newIndex !== null && newIndex !== this.index) {
                this.showAddParticipant = false;
            }
        },
    },
    created() {
        this.appointment = this.appointmentProp;
    },
    methods: {
        toggleAccordion() {
            if (!this.active && this.$refs.accordionItem.isOpen) {
                this.$refs.accordionItem.toggle();
            }
        },
        checkBooking(booking) {
            if (booking.checked) {
                this.checkedBookings.push(booking.id);
            } else {
                this.checkedBookings = this.checkedBookings.filter((id) => id !== booking.id);
            }
        },
        checkWaitList(waitList) {
            if (waitList.checked) {
                this.checkedWaitListEntries.push(waitList.id);
            } else {
                this.checkedWaitListEntries = this.checkedWaitListEntries.filter((id) => id !== waitList.id);
            }
        },
        toggleSelectAll(select) {
            if (select === 'participate') {
                this.selectAllParticipates = !this.selectAllParticipates;
            }
            if (select === 'cancel') {
                this.selectAllCancelings = !this.selectAllCancelings;
            }
            if (select === 'waitList') {
                this.selectAllWaitList = !this.selectAllWaitList;
            }
        },
        editParticipant(event) {
            event.stopPropagation();
            this.showEdit = false;
            this.showAddParticipant = !this.showAddParticipant;
            if (this.showAddParticipant) {
                this.$emit('openSearch', this.index);
            }
        },
        cancelBooking(args) {
            OfferingBookingRepository.cancel(args.id, args.inform)
                .then(() => {
                    this.$notify.success('Die Stornierung war erfolgreich');
                    this.showEdit = false;
                    this.$emit('updateBookings', this.appointment);
                    this.$emit('updateWaitList', this.appointment);
                })
                .catch((error) => {
                    if (error.status === 409) {
                        this.$notify.error(error.message);
                        return;
                    }
                    this.$notify.error('Die Stornierung konnte nicht durchgeführt werden');
                });
        },
        cancelAddParticipant() {
            this.showAddParticipant = false;
        },
        saveParticipant(data) {
            this.loadingAddParticipant = true;
            const promise = this.offering.isBookingModeOffering()
                ? OfferingBookingRepository.addParticipantToOffering(this.clubId, this.offering.id, data, this.selectedSalesListOfferingItems)
                : OfferingBookingRepository.addParticipant(this.clubId, data.id, this.appointment, data.selectedExams, this.selectedSalesListOfferingItems);
            promise
                .then(() => {
                    this.$notify.success('Der Teilnehmer wurde hinzugefügt');
                    this.showAddParticipant = false;
                    this.$emit('updateBookings', this.appointment);
                })
                .catch((error) => this.$notify.error(error.message))
                .finally(() => this.loadingAddParticipant = false);
        },
        saveDogAndParticipant(params) {
            this.loadingAddParticipant = true;
            const promise = this.offering.isBookingModeOffering()
                ? OfferingBookingRepository.addDogAndParticipantToOffering(this.clubId, this.offering.id, params, this.selectedSalesListOfferingItems)
                : OfferingBookingRepository.addDogAndParticipant(this.clubId, params, this.appointment, this.selectedSalesListOfferingItems);
            promise
                .then(() => {
                    this.$notify.success('Der Teilnehmer wurde hinzugefügt');
                    this.showAddParticipant = false;
                    this.$emit('updateBookings', this.appointment);
                })
                .catch((error) => this.$notify.error(error.message))
                .finally(() => this.loadingAddParticipant = false);
        },
        copyToClipboard(content, singleMail = false) {
            if (window.clipboardData && window.clipboardData.setData) {
                return window.clipboardData.setData('Text', content);
            } else if (document.queryCommandSupported && document.queryCommandSupported('copy')) {
                const textarea = document.createElement('textarea');
                textarea.textContent = content;
                textarea.style.position = 'fixed';
                document.body.appendChild(textarea);
                textarea.select();
                try {
                    return document.execCommand('copy');
                } catch (ex) {
                    this.$notify.error('Die Mails konnten nicht in die Zwischenablage kopiert werden');
                } finally {
                    document.body.removeChild(textarea);
                    if (singleMail) {
                        this.$notify.success('{mailAddress} wurde in die Zwischenablage kopiert', { mailAddress: content });
                    } else {
                        this.$notify.success('Die Mails wurden in die Zwischenablage kopiert');
                    }
                }
            } else {
                this.$notify.error('Die Mails konnten nicht in die Zwischenablage kopiert werden');
            }
        },
        openConfirmReactivationModal() {
            this.showReactivationModal = true;
        },
        onReactivationConfirm(payload) {
            this.showReactivationModal = false;
            this.performReactivation(payload.notifyParticipants, payload.notifyClubEmployees);
        },
        onCancelReactivation() {
            this.showReactivationModal = false;
        },
        performReactivation(notifyParticipants, notifyClubEmployees) {
            this.pendingRemove = true;
            AppointmentRepository.reactivateById(this.appointment, notifyParticipants, notifyClubEmployees)
                .then(() => {
                    this.$notify.success('Der Termin wurde erfolgreich reaktiviert');
                    this.appointment.appointmentOverride.isCanceled = false;
                    this.$emit('updateAppointment', this.appointment);
                })
                .catch(() => this.$notify.error('Der Termin konnte nicht reaktiviert werden'));
        },
        openConfirmCancellationModal() {
            this.showCancellationModal = true;
        },
        onCancellationConfirm(payload) {
            this.showCancellationModal = false;
            this.performCancellation(payload.notifyParticipants, payload.notifyClubEmployees);
        },
        onCancelCancellation() {
            this.showCancellationModal = false;
        },
        performCancellation(notifyParticipants, notifyClubEmployees) {
            this.pendingRemove = true;
            AppointmentRepository.updateCancelById(this.appointment, notifyParticipants, notifyClubEmployees)
                .then(() => {
                    this.$notify.success('Der Termin wurde erfolgreich storniert');
                    this.appointment.appointmentOverride.isCanceled = true;
                    this.$emit('updateReplies', this.appointment);
                })
                .catch((error) => {
                    if (error.status === 409) {
                        this.$notify.error(error.message);
                        return;
                    }
                    this.$notify.error('Der Termin konnte nicht storniert werden');
                });
        },
        formattedDateTime(date) {
            return moment(date, 'YYYY-MM-DD HH:mm:ss').format('DD.MM.YYYY HH:mm');
        },
        formattedDate(date) {
            return moment(date, 'YYYY-MM-DD HH:mm:ss').format('YYYY-MM-DD');
        },
        formattedTime(date) {
            return moment(date, 'YYYY-MM-DD HH:mm:ss').format('HH:mm');
        },
        downloadPdf() {
            window.location = `/api/club/offering-bookings/appointment-series/${this.appointment.appointmentSeries.id}/${this.formattedDate(this.appointment.start.date)}/${this.formattedTime(this.appointment.start.date)}/pdf${this.columnQuery}`;
        },
        downloadExcel() {
            window.location = `/api/club/offering-bookings/appointment-series/${this.appointment.appointmentSeries.id}/${this.formattedDate(this.appointment.start.date)}/${this.formattedTime(this.appointment.start.date)}/excel`;
        },
        updateWaitList() {
            this.waitListUpdated = true;
            this.$emit('updateWaitList', this.appointment);
        },
        updateAppointment(appointment) {
            this.appointment = appointment;
            this.showEdit = false;
        },
        showAttendanceList() {
            window.location = `/backend/appointments/${this.appointment.appointmentSeries.id}/${moment(this.appointment.start.date, 'YYYY-MM-DD HH:mm:ss').format('YYYYMMDDHHmm')}/`;
        },
        createExamResults() {
            this.showDialog = true;
        },
        closeDialog() {
            this.showDialog = false;
        },
    },
};
</script>

<style scoped lang="scss" rel="stylesheet/scss">
div.row-min-height {
    min-height: 40px;
}
div.row-extra-margin {
    margin-top: 4px;
}
.add-icon {
    font-size: 1.1rem;
    vertical-align: top;
}
.add-icon-user {
    margin-left: -10px;
    font-size: 1.4rem;
}
.add-icon-dogs {
    margin-left: -8px;
    font-size: 1.4rem;
}
.clear-icon {
    vertical-align: middle;
}
div.search-container {
    display: inline-block;
    vertical-align: bottom;
    font-size: var(--kgr-font-size-text);
}
</style>
